import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { Constants } from "src/app/constants";
import { PhoneStatusEnum } from "src/app/models/phoneStatus";
import { AppConfigService } from "src/app/services/app.config.service";
import { FELoggerService, LogSource } from "src/app/services/feLogger.service";
import { I18Service } from "src/app/services/i18.service";
import { MediaDeviceService } from "src/app/services/media-device.service";
import { MediaStremService } from "src/app/services/media-stream.service";
import { NotificationService } from "src/app/services/notification.service";

@Component({
  selector: 'app-menu-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
})
export class MenuSettingsComponent implements OnInit, OnDestroy {
  private isVisible = false;
  @ViewChild('videoPreview') videoPreviewElement?: ElementRef<HTMLVideoElement>;
  @ViewChild('videoPreviewContainer', { read: ViewContainerRef }) videoPreviewContainer?: ViewContainerRef;
  
  LANGUAGES = I18Service.sortedLanguangeList(Constants.ALLOWED_LANGUAGES);
  selectedLang = Constants.DEFAULT_LANGUAGE;
  selectedAudioOutput = ''; //: MediaDevice | null = null;
  selectedAudioInput = '';
  selectedCamera = '';

  initialSelectedLang = Constants.DEFAULT_LANGUAGE;
  initialSelectedAudioOutput = '' ;//: MediaDevice | null = null;
  initialSelectedAudioInput = '';
  initialSelectedCamera = '';

  loading = true;
  
  // Nuove proprietà per i test
  showAdvancedSettings = false;
  isMicrophoneTesting = false;
  microphoneVolume = 0;
  isSpeakerTesting = false;
  speakerProgress = 0; // Progresso 0-100 per la progress bar
  isVideoPreviewActive = false;
  hasMicrophonePermission = true;
  hasCameraPermission = true;

  private destroy$ = new Subject<void>();
  private microphoneStream?: MediaStream;
  private audioContext?: AudioContext;
  private analyser?: AnalyserNode;
  private microphone?: MediaStreamAudioSourceNode;
  private animationId?: number;
  private videoStream?: MediaStream;
  public audioOutContext?: AudioContext;
  private speakerStartTime = 0;
  private speakerDuration = 3.5; // Durata totale melodia in secondi (allineato al setTimeout)
  private speakerAnimationId?: number;
  public deviceChangeDetected = false;
  phoneStatuses = PhoneStatusEnum;

  constructor(
    public config: AppConfigService,
    private translation: I18Service,
    public mediaService: MediaStremService,
    public mediaDeviceService: MediaDeviceService,
    private dialogRef: MatDialogRef<MenuSettingsComponent>
  ) {}

  ngOnInit(): void {
    // Inizializza quando il componente viene creato
    this.initializeSettings();
    // this.subscribeToDeviceChanges();
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSettings(): void {
    this.loading = true;
    this.selectedLang = this.translation.getLanguage();
    this.mediaService.getUserMediaForEnumerateDevices()
      .then(() => {
        this.loadCurrentSettings();
        this.loading = false;
      })
      .catch((error) => {
        FELoggerService.error('Error getting media devices', LogSource.MediaAudio, error);
        NotificationService.showError(I18Service.get('SNACKBAR.MEDIAPERMISSIONREQUIRED'));
        this.loading = false;
        // Carica comunque le impostazioni anche se non abbiamo i permessi
        this.loadCurrentSettings();
      });
  }

  private loadCurrentSettings(): void {
    this.selectedLang = this.translation.getLanguage();
    
    const preferred = this.mediaDeviceService.getPreferredDevices();
    this.selectedAudioInput = preferred.audioInput || '';// || this.mediaService.audioInputSource || '';
    this.selectedAudioOutput = preferred.audioOutput || '';// || this.mediaService.audioOutputSource;
    this.selectedCamera = preferred.camera || '';// || this.mediaService.videoSource || '';

    this.initialSelectedAudioInput = this.selectedAudioInput;
    this.initialSelectedAudioOutput = this.selectedAudioOutput;
    this.initialSelectedCamera = this.selectedCamera;
    this.initialSelectedLang = this.selectedLang;

    // Controlla i permessi
    this.hasMicrophonePermission = this.mediaService.hasMicrophonePermissionGiven === 1;
    this.hasCameraPermission = this.mediaService.hasCameraPermissionGiven === 1;
  }

  public changeLanguage($event: any): void {
    this.translation.saveLanguage($event.value);
  }

  public changeMediaDevice(type: string, $event: any): void {
    console.log(`change media device '${type}' => ${$event.value}`);
    
    // Ferma eventuali test in corso quando si cambia dispositivo
    if (type === 'audioInput') {
      this.stopMicrophoneTest();
    } else if (type === 'camera') {
      // Se il preview è attivo, riavvialo con la nuova camera
      if (this.isVideoPreviewActive) {
        this.stopVideoPreview();
        // Aspetta che il DOM si aggiorni
        setTimeout(() => {
          this.isVideoPreviewActive = true;
          setTimeout(() => {
            this.startVideoPreview();
          }, 0);
        }, 100);
      }
    }
    
    // this.saveDeviceSettings();
  }

  private saveDeviceSettings(): void {
    this.mediaService.savePreferredDevices({
      audioInput: this.selectedAudioInput, 
      audioOutput: this.selectedAudioOutput, 
      camera: this.selectedCamera
    });
  }


  // Test del microfono
  public async toggleMicrophoneTest(): Promise<void> {
    if (this.isMicrophoneTesting) {
      this.stopMicrophoneTest();
    } else {
      await this.startMicrophoneTest();
    }
  }

  private async startMicrophoneTest(): Promise<void> {
    try {
      const constraints = {
        audio: this.selectedAudioInput ? { deviceId: { exact: this.selectedAudioInput } } : true
      };

      this.microphoneStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.microphone = this.audioContext.createMediaStreamSource(this.microphoneStream);
      
      this.analyser.smoothingTimeConstant = 0.8;
      this.analyser.fftSize = 1024;
      
      this.microphone.connect(this.analyser);
      
      this.isMicrophoneTesting = true;
      this.updateVolumeMeter();
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      this.hasMicrophonePermission = false;
      NotificationService.showError(I18Service.get('SETTINGS.MICROPHONE_ACCESS_ERROR'));
    }
  }

  private updateVolumeMeter(): void {
    if (!this.analyser || !this.isMicrophoneTesting) return;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteFrequencyData(dataArray);

    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      sum += dataArray[i];
    }
    const average = sum / bufferLength;
    this.microphoneVolume = Math.min(100, (average / 255) * 100 * 2);

    this.animationId = requestAnimationFrame(() => this.updateVolumeMeter());
  }

  private stopMicrophoneTest(): void {
    this.isMicrophoneTesting = false;
    
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    
    if (this.microphoneStream) {
      this.microphoneStream.getTracks().forEach(track => track.stop());
      this.microphoneStream = undefined;
    }
    
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = undefined;
    }
    
    this.microphoneVolume = 0;
  }

  // Test degli altoparlanti
  public async testSpeakers(): Promise<void> {
    this.stopTestAudio();
    
    const audioOutContext = new AudioContext();
    
    // Imposta l'output se supportato
    if ('setSinkId' in audioOutContext) {
      try {
        const audioOutputDevice = this.mediaDeviceService.getAudioOutputMediaDevice(this.selectedAudioOutput);
        await (audioOutContext as any).setSinkId(this.selectedAudioOutput);
      } catch (error) {
        console.error('Error setting audio output:', error);
        return
      }
    }
    this.audioOutContext = audioOutContext;
    this.playSimpleMelody(this.audioOutContext);
  }

  private playSimpleMelody(audioContext: AudioContext): void {
    const notes = [
      { frequency: 523.25, duration: 600 },  // Do5 - 4/4 (semibreve)
      { frequency: 587.33, duration: 150 },  // Re5 - 1/4 (croma)
      { frequency: 659.25, duration: 150 },  // Mi5 - 1/4 (croma)
      { frequency: 783.99, duration: 600 },  // Sol5 - 4/4 (semibreve)
      { frequency: 659.25, duration: 300 },  // Mi5 - 2/4 (minima)
      { frequency: 587.33, duration: 150 },  // Re5 - 1/4 (croma)
      { frequency: 523.25, duration: 900 }   // Do5 - finale lungo
    ];

    let currentTime = audioContext.currentTime;

    // Avvia progress bar tracking usando il clock dell'AudioContext
    this.isSpeakerTesting = true;
    this.speakerStartTime = audioContext.currentTime;
    this.speakerProgress = 0;
    this.updateSpeakerProgress(audioContext);

    notes.forEach((note, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = note.frequency;
      oscillator.type = 'sine';

      // Envelope ADSR semplificato per un suono più morbido
      gainNode.gain.setValueAtTime(0, currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, currentTime + 0.02); // Attack
      gainNode.gain.exponentialRampToValueAtTime(0.1, currentTime + note.duration / 1000 - 0.05); // Decay/Sustain
      gainNode.gain.linearRampToValueAtTime(0, currentTime + note.duration / 1000); // Release

      oscillator.start(currentTime);
      oscillator.stop(currentTime + note.duration / 1000);

      currentTime += note.duration / 1000;
    });

    // Chiudi il contesto audio dopo che tutte le note sono finite
    // Durata totale: 600+150+150+600+300+150+900 = 2850ms
    setTimeout(() => {
      this.stopTestAudio();
    }, 3500);
  }

  private updateSpeakerProgress(audioContext: AudioContext): void {
    if (!this.isSpeakerTesting || !audioContext) return;

    const elapsed = audioContext.currentTime - this.speakerStartTime;
    this.speakerProgress = Math.min(100, (elapsed / this.speakerDuration) * 100);

    if (this.speakerProgress < 100) {
      this.speakerAnimationId = requestAnimationFrame(() => this.updateSpeakerProgress(audioContext));
    }
  }


  private stopTestAudio(): void {
    this.isSpeakerTesting = false;

    if (this.speakerAnimationId) {
      cancelAnimationFrame(this.speakerAnimationId);
      this.speakerAnimationId = undefined;
    }

    if (this.audioOutContext) {
      this.audioOutContext.close();
      this.audioOutContext = undefined;
    }

    this.speakerProgress = 0;
  }

  // Preview video
  public async toggleVideoPreview(): Promise<void> {
    if (this.isVideoPreviewActive) {
      this.stopVideoPreview();
    } else {
      // Prima impostiamo il flag, così il template mostra il video element
      this.isVideoPreviewActive = true;
      // Poi aspettiamo il prossimo ciclo di change detection
      setTimeout(() => {
        this.startVideoPreview();
      }, 0);
    }
  }

  private async startVideoPreview(): Promise<void> {
    // Ora l'elemento dovrebbe esistere
    if (!this.videoPreviewElement) {
      console.error('Video preview element not found');
      this.isVideoPreviewActive = false;
      return;
    }
    
    try {
      const constraints = {
        video: this.selectedCamera 
          ? ( this.config.isMobilePhone ? 
              { facingMode: this.selectedCamera } : 
              { deviceId: { exact: this.selectedCamera } } 
            )
          : true
      };

      this.videoStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.videoPreviewElement.nativeElement.srcObject = this.videoStream;
      this.hasCameraPermission = true;
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      // this.hasCameraPermission = false;
      this.isVideoPreviewActive = false;
      NotificationService.showError(I18Service.get('SETTINGS.CAMERA_ACCESS_ERROR'));
    }
  }

  private stopVideoPreview(): void {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = undefined;
    }
    
    if (this.videoPreviewElement) {
      this.videoPreviewElement.nativeElement.srcObject = null;
    }
    
    this.isVideoPreviewActive = false;
  }

  public shouldMirrorVideo(): boolean {
    return this.selectedCamera === 'user' || 
           (!this.config.isMobilePhone && this.selectedCamera !== 'environment');
  }

  private stopAllTests(): void {
    this.stopMicrophoneTest();
    this.stopVideoPreview();
    this.stopTestAudio();
  }

  private cleanup(): void {
    this.stopAllTests();
  }

  // Gestione chiusura dialog
  public onCancel(): void {
    // Verifica se ci sono modifiche non salvate
    if (this.hasChanges()) {
      // Chiedi conferma prima di uscire senza salvare
      NotificationService.popupQuestion({
        msg: I18Service.get('SETTINGS.UNSAVED_CHANGES_WARNING'),
        confirmButton: I18Service.get('SETTINGS.EXIT_WITHOUT_SAVING'),
        cancelButton: I18Service.get('POPUP.CANCEL')
      }).then((result) => {
        if (result.isConfirmed) {
          // Ripristina la lingua originale (è stata già salvata al cambio dropdown)
          if (this.selectedLang !== this.initialSelectedLang) {
            this.translation.saveLanguage(this.initialSelectedLang);
          }
          // Chiudi senza salvare i dispositivi (le modifiche UI vengono perse)
          this.close();
        }
        // Altrimenti rimani nel dialog
      });
    } else {
      // Nessuna modifica: chiudi direttamente
      this.close();
    }
  }


  public resetToDefault(): void {
    // Reset lingua al default (applica subito per feedback visivo)
    this.selectedLang = this.config.defaultLang;
    this.translation.saveLanguage(this.selectedLang);

    // Aggiorna UI dispositivi con i valori di default (primo della lista)
    // NON salva ancora! L'utente dovrà premere "Salva" per applicare
    // const firstAudioIn = this.mediaDeviceService.getDefaultDevice('audioinput');
    // const firstAudioOut = this.mediaDeviceService.getDefaultDevice('audiooutput');
    // const firstCamera = this.mediaDeviceService.getDefaultDevice('videoinput');

    this.selectedAudioInput = '';
    this.selectedAudioOutput = '';
    this.selectedCamera = '';

    // NON aggiorna gli initial* → hasChanges() ritornerà true
    // (tranne per la lingua che è già salvata)
    this.initialSelectedLang = this.selectedLang;

    NotificationService.show(I18Service.get('SETTINGS.DEFAULTS_LOADED'));
  }

  private close(): void {
    this.cleanup();
    this.dialogRef.close(false);
  }

  public hasChanges(): boolean {
    return this.selectedLang !== this.initialSelectedLang ||
           this.selectedAudioInput !== this.initialSelectedAudioInput ||
           this.selectedAudioOutput !== this.initialSelectedAudioOutput ||
           this.selectedCamera !== this.initialSelectedCamera;
  }

  public saveChanges(): void {
    this.saveDeviceSettings();
    this.initialSelectedAudioInput = this.selectedAudioInput;
    this.initialSelectedAudioOutput = this.selectedAudioOutput;
    this.initialSelectedCamera = this.selectedCamera;
    this.initialSelectedLang = this.selectedLang;
    
    NotificationService.showSuccess(I18Service.get('SETTINGS.CHANGES_SAVED'));
    this.close();
  }

}